import "core-js/modules/es6.array.filter";
import "core-js/modules/es6.regexp.replace";
import "core-js/modules/es6.array.for-each";
import "core-js/modules/es6.array.find";
import "core-js/modules/es6.array.map";
import "core-js/modules/es6.array.find-index";
import "core-js/modules/es6.array.some";
import "core-js/modules/es6.array.reduce";
import "core-js/modules/es6.function.name";
import "core-js/modules/es6.array.index-of";
import "core-js/modules/es6.object.assign";

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
/* eslint-disable default-case */


import { autorun } from "mobx";
import { types, getParentOfType, getSnapshot, applySnapshot, onSnapshot as _onSnapshot, getEnv, destroy } from "mobx-state-tree";
import { trim, trimStart, startsWith, isNil, isPlainObject, isEmpty } from "lodash-es";
import { monaco } from "@monaco-editor/react";
import { Parser } from "@reactivepad/formula-parser";
import { generateId, parseReferenceString, getNewFormulaName, getNewTableName, getNewColumnName, getFormattedValue, standardizeInput, isFormula, validateNodeName, hasCircularDependency, hasCircularDependencyTableColumn, mapColumnsToCells, createAndFillGrid, prependWithFormulaSign, reorderArrayElements } from "./utils";
import { rename, tokenize } from "./parser";
import { SUPPORTED_FUNCTIONS } from "./functions";
import { toJS } from "mobx";
import { errors } from "./common";
var DEFAULT_ROW_COUNT = 4;

function ValidationError(field, code) {
  this.field = field;
  this.code = code;
}

export var FormulaModel = types.model("FormulaModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: "formula",
  name: types.string,
  value: types.frozen(null),
  formatted: types.frozen(null),
  formatter: types.maybeNull(types.string)
})["volatile"](function () {
  return {
    isEditing: false
  };
}).views(function (self) {
  return {
    get store() {
      return getParentOfType(self, Store);
    },

    get isFormula() {
      return isFormula(self.value);
    },

    get parsed() {
      if (!self.isFormula) {
        return {
          result: self.value,
          error: null
        };
      }

      return self.store.parser.parse("" + trimStart(self.value, "="));
    },

    get _formatted() {
      var _self$parsed = self.parsed,
          result = _self$parsed.result,
          error = _self$parsed.error;

      if (error) {
        return error;
      }

      return getFormattedValue(result, self.formatter);
    },

    serialize: function serialize() {
      var toJSON = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      return toJSON ? getSnapshot(self) : toJS(self);
    },
    onSnapshot: function onSnapshot(callback) {
      return _onSnapshot(self, callback);
    }
  };
}).actions(function (self) {
  var formattedDisposer = void 0;
  return {
    setName: function setName(_name) {
      var oldName = self.name;
      var name = trim(_name);

      if (oldName === name) {
        return;
      }

      if (!validateNodeName(name)) {
        // ToDo: change to formula_name
        throw new ValidationError("name", errors.name_invalid);
      }

      var existingNode = self.store.findByName(name);

      if (existingNode && existingNode.id !== self.id) {
        throw new ValidationError("name", errors.name_exists);
      }

      self.name = name;
      self.store.renameNode(self, oldName, name);
    },
    setValue: function setValue(_value) {
      var value = standardizeInput(_value);

      if (!isNil(value) && isFormula(value) && hasCircularDependency(self.name, value, self.store)) {
        throw new ValidationError("value", errors.circular_dependency);
      }

      self.value = value;
    },
    setFormatted: function setFormatted(formatted) {
      self.formatted = formatted;
    },
    setFormatter: function setFormatter(formatter) {
      self.formatter = formatter;
    },
    toggleEditing: function toggleEditing() {
      var isEditing = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.isEditing;
      var _self$store = self.store,
          editingNode = _self$store.editingNode,
          isSomeNodeEditing = _self$store.isSomeNodeEditing;

      if (isSomeNodeEditing && !self.isEditing) {
        editingNode.toggleEditing(false);
      }

      self.isEditing = isEditing;
    },
    setProps: function setProps(props) {
      if (!props) return;
      applySnapshot(self, _extends({}, self.serialize(), props));
    },
    destroy: function destroy() {
      self.store.removeNode(self);
    },
    afterCreate: function afterCreate() {
      formattedDisposer = autorun(function () {
        self.setFormatted(self._formatted);
      });
    },
    beforeDestroy: function beforeDestroy() {
      if (formattedDisposer) {
        formattedDisposer();
      }
    }
  };
});
export var ColumnModel = types.model("ColumnModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  name: types.string,
  type: "column",
  columnValue: types.frozen(null),
  footerValue: types.frozen(null),
  footerFormatted: types.frozen(null),
  formatter: types.maybeNull(types.string),
  width: 150
}).views(function (self) {
  return {
    get store() {
      return getParentOfType(self, Store);
    },

    get table() {
      return getParentOfType(self, TableModel);
    },

    get hasColumnValue() {
      return !isNil(self.columnValue);
    },

    get isColumnValueFormula() {
      return self.hasColumnValue && isFormula(self.columnValue);
    },

    get footerParsed() {
      if (!isFormula(self.footerValue)) {
        return {
          result: self.footerValue,
          error: null
        };
      }

      return self.store.parser.parse("" + trimStart(self.footerValue, "="));
    },

    get _footerFormatted() {
      var _self$footerParsed = self.footerParsed,
          result = _self$footerParsed.result,
          error = _self$footerParsed.error;

      if (error) {
        return error;
      }

      return getFormattedValue(result, self.formatter);
    },

    getColumnCells: function getColumnCells() {
      return self.table.rows.reduce(function (acc, row) {
        var cell = row.findCellByColumnId(self.id);
        acc.push(cell);
        return acc;
      }, []);
    },
    getColumnCellByRowIndex: function getColumnCellByRowIndex(rowIndex) {
      if (rowIndex >= self.table.rows.length) {
        return undefined;
      }

      return self.table.rows[rowIndex].findCellByColumnId(self.id);
    }
  };
}).actions(function (self) {
  var footerDisposer = void 0;
  var footerFormattedDisposer = void 0;
  return {
    setName: function setName(_name) {
      var oldName = self.name;
      var newName = trim(_name);

      if (oldName === newName) {
        return;
      }

      if (!newName) {
        throw new ValidationError("column_name", errors.column_empty_name);
      }

      var columnNameExists = self.table.columns.some(function (_ref) {
        var name = _ref.name;
        return name === newName;
      });

      if (columnNameExists) {
        throw new ValidationError("column_name", errors.column_name_exists);
      }

      if (!/^[\w%\[\]\(\)\$ ]+$/.test(newName)) {
        throw new ValidationError("column_name", // ToDo: extract constants
        errors.column_name_not_allowed);
      }

      if (newName.length > 16) {
        throw new ValidationError("column_name", errors.column_name_too_long);
      }

      self.name = newName;
      self.table.store.renameColumn(self.table, self, oldName, self.name);
    },
    setColumnValue: function setColumnValue(value) {
      if (isFormula(value) && hasCircularDependencyTableColumn(self.name, value, self.table.name)) {
        throw new ValidationError("column_formula", // ToDo: change to column_value
        errors.column_formula_circular_dependency);
      }

      self.columnValue = value;
    },
    removeColumnValue: function removeColumnValue() {
      self.columnValue = null;
    },
    setFormatter: function setFormatter(formatter) {
      self.formatter = formatter;
    },
    setWidth: function setWidth(width) {
      self.width = width;
    },
    setFooterValue: function setFooterValue(footerValue) {
      self.footerValue = footerValue;
    },
    setFooterFormatted: function setFooterFormatted(footerFormatted) {
      self.footerFormatted = footerFormatted;
    },
    remove: function remove() {
      self.table.removeColumn(self.id);
    },
    afterCreate: function afterCreate() {
      footerDisposer = autorun(function () {
        if (!self.table.showFooter) {
          self.setFooterValue(null);
        }
      });
      footerFormattedDisposer = autorun(function () {
        self.setFooterFormatted(self._footerFormatted);
      });
    },
    beforeDestroy: function beforeDestroy() {
      if (footerDisposer) {
        footerDisposer();
      }

      if (footerFormattedDisposer) {
        footerFormattedDisposer();
      }
    }
  };
});
export var CellModel = types.model("CellModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: "cell",
  column: types.safeReference(ColumnModel),
  value: types.frozen(null),
  formatted: types.frozen(null)
}).views(function (self) {
  return {
    get row() {
      return getParentOfType(self, RowModel);
    },

    get index() {
      return self.row.cells.findIndex(function (_ref2) {
        var id = _ref2.id;
        return id === self.id;
      });
    },

    get rowIndex() {
      return self.table.rows.findIndex(function (_ref3) {
        var id = _ref3.id;
        return id === self.row.id;
      });
    },

    get table() {
      return self.row.table;
    },

    get parsed() {
      var _self$column = self.column,
          hasColumnValue = _self$column.hasColumnValue,
          isColumnValueFormula = _self$column.isColumnValueFormula,
          columnValue = _self$column.columnValue;

      if (!hasColumnValue) {
        return {
          result: self.value,
          error: null
        };
      }

      if (!isColumnValueFormula) {
        return {
          result: columnValue,
          error: null
        };
      } // Replicating Coda / AirTable behaviour here
      // If cell refers to other column (i.e. table1[Column1])
      // it takes the value of the cell from the same row
      // ToDo: test if works for more complicated formulas like table1[[foo]:[bar]]


      var tokens = tokenize(columnValue).map(function (token) {
        if (!isPlainObject(token)) {
          return token;
        }

        if (!token.type || token.type !== "table" || token.name !== self.table.name) {
          return token.text;
        }

        return "INDEX(" + token.text + ", " + (self.rowIndex + 1) + ")";
      });
      var joined = tokens.join("");
      return self.table.store.parser.parse("" + joined);
    },

    get _formatted() {
      if (!self.column.hasColumnValue) {
        return getFormattedValue(self.value, self.column.formatter);
      }

      var _self$parsed2 = self.parsed,
          result = _self$parsed2.result,
          error = _self$parsed2.error;

      if (error) {
        return error;
      }

      return getFormattedValue(result, self.column.formatter);
    }

  };
}).actions(function (self) {
  var valueDisposer = void 0;
  var formattedDisposer = void 0;
  return {
    setValue: function setValue(value) {
      self.value = standardizeInput(value);
    },
    setFormatted: function setFormatted(formatted) {
      self.formatted = formatted;
    },
    afterCreate: function afterCreate() {
      if (!self.column) {
        self.column = self.table.columns[self.index];
      }

      valueDisposer = autorun(function () {
        // detect when column value is set and reset cell
        if (self.column.hasColumnValue) {
          self.setValue(null);
        }
      });
      formattedDisposer = autorun(function () {
        self.setFormatted(self._formatted);
      });
    },
    beforeDestroy: function beforeDestroy() {
      valueDisposer();
      formattedDisposer();
    }
  };
});
export var RowModel = types.model("RowModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: "row",
  cells: types.array(CellModel),
  height: 20
}).views(function (self) {
  return {
    get table() {
      return getParentOfType(self, TableModel);
    },

    findCellByColumnId: function findCellByColumnId(columnId) {
      return self.cells.find(function (_ref4) {
        var column = _ref4.column;
        return column.id === columnId;
      });
    },
    findCellById: function findCellById(cellId) {
      return self.cells.find(function (_ref5) {
        var id = _ref5.id;
        return id === cellId;
      });
    }
  };
}).actions(function (self) {
  return {
    setHeight: function setHeight(height) {
      self.height = height;
    }
  };
});
export var TableModel = types.model("TableModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: "table",
  name: types.string,
  columns: types.array(ColumnModel),
  rows: types.array(RowModel),
  showFooter: false
}).views(function (self) {
  return {
    get store() {
      return getParentOfType(self, Store);
    },

    getColumnById: function getColumnById(id) {
      return self.columns.find(function (c) {
        return c.id === id;
      });
    },
    getColumnByName: function getColumnByName(name) {
      return self.columns.find(function (c) {
        return c.name === name;
      });
    },
    getColumnIndexById: function getColumnIndexById(id) {
      return self.columns.findIndex(function (c) {
        return c.id === id;
      });
    },
    serialize: function serialize() {
      var toJSON = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      return toJSON ? getSnapshot(self) : toJS(self);
    },
    onSnapshot: function onSnapshot(callback) {
      return _onSnapshot(self, callback);
    }
  };
}).actions(function (self) {
  return {
    setName: function setName(_name) {
      var oldName = self.name;
      var name = trim(_name);

      if (oldName === name) {
        return;
      }

      if (!validateNodeName(name)) {
        throw new Error(errors.name_invalid);
      }

      var existingNode = self.store.findByName(name);

      if (existingNode && existingNode.id !== self.id) {
        throw new Error(errors.name_exists);
      }

      self.name = name;
      self.store.renameNode(self, oldName, name);
    },
    addColumn: function addColumn() {
      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : self.columns.length;
      var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (!props.name) {
        props.name = getNewColumnName(self);
      }

      var column = ColumnModel.create(props);
      self.columns.splice(index, 0, column);
      self.rows.forEach(function (_ref6) {
        var cells = _ref6.cells;
        cells.splice(index, 0, {
          column: column.id
        });
      });
    },
    removeColumn: function removeColumn(id) {
      if (self.columns.length === 1 && self.columns[0].id === id) {
        self.destroy();
        return;
      }

      var columnIndex = self.getColumnIndexById(id);
      self.rows.forEach(function (_ref7) {
        var cells = _ref7.cells;
        cells.splice(columnIndex, 1);
      });
      self.columns.splice(columnIndex, 1);
    },
    reorderColumns: function reorderColumns(oldIndex, newIndex, length) {
      self.columns = reorderArrayElements(self.columns, oldIndex, newIndex, length);
      self.syncCellsOrderWithColumns();
    },
    syncCellsOrderWithColumns: function syncCellsOrderWithColumns() {
      self.rows.forEach(function (row, i) {
        var orderedCells = self.columns.map(function (col) {
          return col.getColumnCellByRowIndex(i);
        });
        row.cells.replace(orderedCells);
      });
    },
    insertRow: function insertRow() {
      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : self.rows.length;
      var row = arguments[1];
      self.rows.splice(index, 0, row || {
        cells: mapColumnsToCells(self.columns)
      });
    },
    removeRow: function removeRow() {
      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : self.rows.length - 1; // ToDo: improve behaviour when removing last row (remove whole table like in case of column?)

      if (self.rows.length === 1 || index >= self.rows.length) {
        return;
      }

      self.rows.splice(index, 1);
    },
    toggleFooter: function toggleFooter() {
      var showFooter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.showFooter;
      self.showFooter = showFooter;
    },
    destroy: function destroy() {
      self.store.removeNode(self);
    },
    setProps: function setProps(props) {
      if (!props) return;
      applySnapshot(self, _extends({}, self.serialize(), props));
    }
  };
});
var FormulaTableUnion = types.union({
  dispatcher: function dispatcher(_ref8) {
    var type = _ref8.type;
    return type === "formula" ? FormulaModel : TableModel;
  }
}, FormulaModel, TableModel);
var Store = types.model("StoreModel", {
  nodes: types.array(FormulaTableUnion),
  readOnly: false
}).views(function (self) {
  return {
    get formulas() {
      return self.nodes.filter(function (_ref9) {
        var type = _ref9.type;
        return type === "formula";
      });
    },

    get tables() {
      return self.nodes.filter(function (_ref10) {
        var type = _ref10.type;
        return type === "table";
      });
    },

    get isEmpty() {
      return self.nodes.length === 0;
    },

    // ToDo: change to "editingFormula"
    get editingNode() {
      return self.formulas.find(function (_ref11) {
        var isEditing = _ref11.isEditing;
        return isEditing;
      });
    },

    // ToDo: change to "isSomeFormulaEditing"
    get isSomeNodeEditing() {
      return !!self.editingNode;
    },

    get functions() {
      var functionsFromEnv = getEnv(self).functions;

      if (!functionsFromEnv || isEmpty(functionsFromEnv)) {
        return SUPPORTED_FUNCTIONS;
      }

      return SUPPORTED_FUNCTIONS.filter(function (_ref12) {
        var name = _ref12.name;
        return functionsFromEnv.some(function (fn) {
          return fn.toUpperCase() === name.toUpperCase();
        });
      });
    },

    findById: function findById(id) {
      return self.nodes.find(function (n) {
        return n.id === id;
      });
    },
    findByName: function findByName(name) {
      return self.nodes.find(function (n) {
        return n.name === name;
      });
    },
    onNodesSnapshot: function onNodesSnapshot(callback) {
      return _onSnapshot(self.nodes, callback);
    }
  };
})["volatile"](function (self) {
  var parser = new Parser(); // ToDo: handle lowercase function name

  parser.setFunction("INDEX", function (_ref13) {
    var array = _ref13[0],
        index = _ref13[1];
    return array[index - 1];
  });
  parser.setFunction("UPPER", function (_ref14) {
    var text = _ref14[0];
    return text && text.toUpperCase();
  });
  parser.on("callVariable", function (reference, done) {
    // ToDo: handle more complex table cases, i.e. table1[[foo]:[bar]]
    // ToDo: handle whole table
    var parsedReference = parseReferenceString(reference);
    var node = self.findByName(parsedReference.name);

    if (!node) {
      // ToDo: Is it better to "return" or to call done(null)? Ask Handsontable
      return;
    }

    switch (parsedReference.type) {
      case "formula":
        {
          var _isFormula = node.isFormula,
              result = node.parsed.result;
          var resolved = _isFormula ? result : isNil(result) ? 0 : JSON.stringify(result);
          done(resolved);
          break;
        }

      case "table":
        {
          var column = node.getColumnByName(parsedReference.column);

          if (!column) {
            return;
          }

          var cells = column.getColumnCells().map(function (_ref15) {
            var parsed = _ref15.parsed;
            return isNil(parsed.result) ? 0 : parsed.result;
          });
          done(cells);
          break;
        }

      default:
        {// Todo: handle default type
        }
    }
  });
  return {
    parser: parser,
    monacoEditorPromise: null
  };
}).actions(function (self) {
  return {
    createAndAddFormula: function createAndAddFormula() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _props$name = props.name,
          name = _props$name === undefined ? getNewFormulaName(self.nodes) : _props$name,
          rest = _objectWithoutProperties(props, ["name"]);

      var formula = FormulaModel.create(_extends({}, rest, {
        name: name
      }));
      self.nodes.push(formula);
      return formula;
    },
    createAndAddTable: function createAndAddTable() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _props$name2 = props.name,
          name = _props$name2 === undefined ? getNewTableName(self.nodes, getEnv(self).tableNamePrefix) : _props$name2,
          _cols = props.columns,
          _rows = props.rows,
          rest = _objectWithoutProperties(props, ["name", "columns", "rows"]);

      var columns = (_cols || [{
        name: "Column1"
      }, {
        name: "Column2"
      }, {
        name: "Column3"
      }]).map(function (c) {
        return ColumnModel.create(c);
      });

      var rows = _rows || createAndFillGrid(DEFAULT_ROW_COUNT, columns);

      var table = TableModel.create(_extends({}, rest, {
        name: name,
        columns: columns,
        rows: rows
      }));
      self.nodes.push(table);
      return table;
    },
    removeNode: function removeNode(node) {
      destroy(node);
    },
    renameNode: function renameNode(node, oldName, newName) {
      self.nodes.forEach(function (n) {
        if (node.type === "formula" && node.id === n.id) {
          // Skip formula that is triggering rename (it has already been renamed)
          return;
        }

        switch (n.type) {
          case "table":
            {
              n.columns.forEach(function (col) {
                if (!col.isColumnValueFormula) {
                  return;
                }

                var renamed = rename(col.columnValue, node.type, oldName, newName);
                col.setColumnValue(prependWithFormulaSign(renamed));
              });
              break;
            }

          case "formula":
            {
              if (n.isFormula) {
                var renamed = rename(n.value, node.type, oldName, newName);
                n.setValue(prependWithFormulaSign(renamed));
              }

              break;
            }
        }
      });
    },
    renameColumn: function renameColumn(table, _column, oldName, newName) {
      self.nodes.forEach(function (node) {
        switch (node.type) {
          case "table":
            {
              var columns = node.columns;
              columns.forEach(function (col) {
                if (col.id === _column.id && !isNil(col.footerValue)) {
                  // if it's the same column, update just footer
                  var renamed = rename(col.footerValue, "column", oldName, newName, table.name);
                  col.setFooterValue(startsWith(col.footerValue, "=") ? prependWithFormulaSign(renamed) : renamed);
                  return;
                }

                if (col.isColumnValueFormula) {
                  var _renamed = rename(col.columnValue, "column", oldName, newName, table.name);

                  col.setColumnValue(prependWithFormulaSign(_renamed));
                }
              });
              break;
            }

          case "formula":
            {
              var renamed = rename(node.value, "column", oldName, newName, table.name);
              node.setValue(prependWithFormulaSign(renamed));
              break;
            }
        }
      });
    },
    setReadOnly: function setReadOnly() {
      var readOnly = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.readOnly;
      self.readOnly = readOnly;
    },
    initMonacoEditor: function initMonacoEditor() {
      self.monacoEditorPromise = monaco.init();
      return self.monacoEditorPromise;
    },
    beforeDestroy: function beforeDestroy() {
      self.parser.off("callVariable");
    }
  };
});
export { Store };